import { useCallback, useEffect, useRef, useState } from "react";
import {
  MEDIA_RECORDER_CHUNK_SECONDS,
  MEDIA_RECORDER_TIMESLICE_MS,
  RESUME_CHUNK_INDEX_BUFFER,
} from "../constants";
import { RetailRecordingStatus } from "../types";
import {
  isRecordingSupported,
  resolveSupportedAudioMimeType,
} from "../utils/recordingMediaUtils";
import {
  getActiveSessionForToday,
  SESSION_STORAGE_KEY,
} from "../utils/recordingSessionStorage";
import { clearStaleRecordingData } from "../utils/recordingChunkStorage";
import {
  claimLeadershipNow,
  requestStop,
  subscribeToStopRequests,
} from "../utils/recordingTabCoordinator";
import { getUploadFailureMessage } from "../utils/recordingErrors";
import { reportRecordingError } from "../utils/reportRecordingError";
import { useChunkedAudioUpload } from "./useChunkedAudioUpload";
import { useRecordingTabLeadership } from "./useRecordingTabLeadership";
import { useHybridToken } from "providers/Auth";

type UseRetailRecordingOptions = {
  contactId: string;
};

type UseRetailRecordingResult = {
  status: RetailRecordingStatus;
  errorMessage: string | null;
  elapsedSeconds: number;
  startRecording: () => void;
  stopRecording: () => void;
  dismissRecordingPrompt: () => void;
  requireRecordingPrompt: () => void;
};

const isPermissionError = (error: unknown): boolean =>
  error instanceof DOMException &&
  (error.name === "NotAllowedError" || error.name === "SecurityError");

const useRetailRecording = ({
  contactId,
}: UseRetailRecordingOptions): UseRetailRecordingResult => {
  const isLeader = useRecordingTabLeadership();
  const hybridToken = useHybridToken();

  const [status, setStatus] = useState<RetailRecordingStatus>("checking");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [sessionVersion, setSessionVersion] = useState(0);

  // Mirrors `status` synchronously for use inside effects/callbacks so we
  // never act on a stale closure over React state, and so the "auto-resume
  // when this tab becomes leader" effect can tell it shouldn't clobber an
  // in-flight manual start/stop triggered from this same tab.
  const phaseRef = useRef<RetailRecordingStatus>("checking");
  useEffect(() => {
    phaseRef.current = status;
  }, [status]);

  const hybridTokenRef = useRef(hybridToken);
  hybridTokenRef.current = hybridToken;
  const getHybridToken = useCallback(() => hybridTokenRef.current, []);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const elapsedTimerRef = useRef<number | null>(null);

  const stopMediaTracks = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const clearElapsedTimer = useCallback(() => {
    if (elapsedTimerRef.current !== null) {
      window.clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  const startElapsedTimer = useCallback(() => {
    clearElapsedTimer();
    elapsedTimerRef.current = window.setInterval(() => {
      if (recordingStartedAtRef.current === null) {
        return;
      }
      setElapsedSeconds(
        Math.floor((Date.now() - recordingStartedAtRef.current) / 1000)
      );
    }, 1000);
  }, [clearElapsedTimer]);

  const stopCapturing = useCallback(async () => {
    clearElapsedTimer();

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
        recorder.stop();
      });
    }
    mediaRecorderRef.current = null;
    stopMediaTracks();
  }, [clearElapsedTimer, stopMediaTracks]);

  const handleFatalUploadFailure = useCallback(
    async (error: unknown) => {
      if (phaseRef.current !== "recording" && phaseRef.current !== "starting") {
        return;
      }

      setErrorMessage(getUploadFailureMessage(error));
      setStatus("error");
      await stopCapturing();
      recordingStartedAtRef.current = null;
      setElapsedSeconds(0);
    },
    [stopCapturing]
  );

  const {
    startUploadSession,
    resumeUploadSession,
    enqueueChunk,
    finalizeUpload,
  } = useChunkedAudioUpload({
    contactId,
    getHybridToken,
    onUploadFatalError: handleFatalUploadFailure,
  });

  // Wires up a freshly acquired MediaStream to a new MediaRecorder and
  // starts it. Shared by both the "start brand-new recording" and "resume
  // an existing recording" paths.
  const attachAndStartRecorder = useCallback(
    (stream: MediaStream, mimeType: string, recordingId: string) => {
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          enqueueChunk(event.data).catch(() => {
            // Upload errors are reported and handled in useChunkedAudioUpload.
          });
        }
      };

      recorder.onerror = (event) => {
        // Some browsers expose a DOMException on `event.error`; the DOM lib
        // types the handler arg as a plain Event, so read it defensively.
        const mediaRecorderError = (event as { error?: unknown }).error;
        reportRecordingError(
          "Retail recording MediaRecorder error",
          mediaRecorderError ?? event,
          { contactId, recordingId }
        );
      };

      recorder.start(MEDIA_RECORDER_TIMESLICE_MS);
      recordingStartedAtRef.current = Date.now();
      setElapsedSeconds(0);
      startElapsedTimer();
    },
    [contactId, enqueueChunk, startElapsedTimer]
  );

  // Auto-resume: whenever this tab is (or becomes) the leader, pick up
  // today's active session, if any, without requiring the rep to click
  // anything. Guarded against clobbering an in-flight manual start/stop
  // kicked off from this same tab.
  useEffect(() => {
    if (!isLeader) {
      return;
    }
    if (
      phaseRef.current === "starting" ||
      phaseRef.current === "recording" ||
      phaseRef.current === "stopping" ||
      phaseRef.current === "error"
    ) {
      return;
    }

    const session = getActiveSessionForToday();
    if (!session) {
      if (phaseRef.current !== "dismissed") {
        setStatus("prompting");
      }
      return;
    }

    setStatus("starting");
    setErrorMessage(null);

    (async () => {
      if (!isRecordingSupported()) {
        throw new Error("MediaRecorder is not supported in this browser");
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeType = resolveSupportedAudioMimeType(stream);
      if (!mimeType) {
        stopMediaTracks();
        throw new Error(
          "No supported audio recording format available on this browser"
        );
      }

      const resumeChunkIndex =
        session.nextChunkIndex + RESUME_CHUNK_INDEX_BUFFER;
      await resumeUploadSession(
        session.recordingId,
        mimeType,
        resumeChunkIndex
      );
      attachAndStartRecorder(stream, mimeType, session.recordingId);
      setStatus("recording");
    })().catch((error) => {
      reportRecordingError("Retail recording resume failed", error, {
        contactId,
      });
      setErrorMessage(
        isPermissionError(error)
          ? "Microphone access is required to continue recording. Please allow microphone access and try again."
          : getUploadFailureMessage(error)
      );
      setStatus("error");
    });
    // sessionVersion intentionally triggers a re-run when another tab
    // starts/stops the day's recording (see the storage listener below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeader, sessionVersion]);

  // Followers never touch the microphone; they just reflect whether a
  // recording exists for today, based on the shared localStorage session.
  useEffect(() => {
    if (isLeader) {
      return;
    }
    const session = getActiveSessionForToday();
    setStatus(
      session
        ? "recording_follower"
        : phaseRef.current === "dismissed"
        ? "dismissed"
        : "prompting"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeader, sessionVersion]);

  // Other tabs starting/stopping the day's recording write to the shared
  // session key; react to that here so this tab's UI stays in sync.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === SESSION_STORAGE_KEY) {
        setSessionVersion((version) => version + 1);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Best-effort cleanup of abandoned recordings (interrupted uploads that were
  // never finalized). Runs once on mount and only removes data older than 24h,
  // so an in-progress or same-day recording is never touched.
  useEffect(() => {
    clearStaleRecordingData().catch((error) => {
      reportRecordingError(
        "Retail recording stale data cleanup failed",
        error,
        { contactId }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = useCallback(() => {
    setErrorMessage(null);
    setStatus("starting");

    if (!isRecordingSupported()) {
      setErrorMessage(
        "This browser doesn't support audio recording. Please switch to a supported browser."
      );
      setStatus("error");
      return;
    }

    // A user gesture in this tab should make this tab the one driving the
    // recording, regardless of any prior/stale leadership state.
    claimLeadershipNow();

    (async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeType = resolveSupportedAudioMimeType(stream);
      if (!mimeType) {
        stopMediaTracks();
        throw new Error(
          "No supported audio recording format available on this browser"
        );
      }

      const recordingId = await startUploadSession(
        mimeType,
        MEDIA_RECORDER_CHUNK_SECONDS
      );
      attachAndStartRecorder(stream, mimeType, recordingId);
      setStatus("recording");
    })().catch((error) => {
      reportRecordingError("Retail recording start failed", error, {
        contactId,
      });
      stopMediaTracks();
      setErrorMessage(
        isPermissionError(error)
          ? "Microphone access is required to continue. Please allow microphone access and try again."
          : getUploadFailureMessage(error)
      );
      setStatus("error");
    });
  }, [attachAndStartRecorder, contactId, startUploadSession, stopMediaTracks]);

  const stopRecording = useCallback(() => {
    if (!isLeader) {
      // Only the leader tab holds the microphone/MediaRecorder; ask it to
      // stop. Its finalize will clear the shared session, which flips this
      // tab back to "prompting" via the storage listener above.
      requestStop();
      return;
    }

    setStatus("stopping");

    (async () => {
      await stopCapturing();
      await finalizeUpload();
    })()
      .catch((error) => {
        reportRecordingError("Retail recording stop failed", error, {
          contactId,
        });
      })
      .finally(() => {
        recordingStartedAtRef.current = null;
        setElapsedSeconds(0);
        setStatus("prompting");
      });
  }, [contactId, finalizeUpload, isLeader, stopCapturing]);

  const dismissRecordingPrompt = useCallback(() => {
    if (phaseRef.current === "prompting" || phaseRef.current === "error") {
      setErrorMessage(null);
      setStatus("dismissed");
    }
  }, []);

  const requireRecordingPrompt = useCallback(() => {
    if (phaseRef.current === "dismissed") {
      setStatus("prompting");
    }
  }, []);

  // A follower tab asked us (the leader) to stop the recording.
  useEffect(() => {
    if (!isLeader) {
      return undefined;
    }
    return subscribeToStopRequests(stopRecording);
  }, [isLeader, stopRecording]);

  // Warn before closing/refreshing while this tab is actively recording.
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (phaseRef.current !== "recording") {
        return;
      }
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    return () => {
      clearElapsedTimer();
      mediaRecorderRef.current?.stop();
      stopMediaTracks();
    };
  }, [clearElapsedTimer, stopMediaTracks]);

  return {
    status,
    errorMessage,
    elapsedSeconds,
    startRecording,
    stopRecording,
    dismissRecordingPrompt,
    requireRecordingPrompt,
  };
};

export { useRetailRecording };
